// A config object in, DOM out. No copy of its own -- every visible string comes
// from the config, which the server built from Signups::FormDefinition.
//
// Every node here is createElement + textContent. There is no innerHTML in this
// file and there must never be one: this renders server-supplied strings on
// every website that embeds a form.
//
// NO DESTRUCTURING ANYWHERE IN THIS DIRECTORY, which is why the options
// arguments below are read a property at a time. esbuild refuses to downlevel
// destructuring for our --target floor (safari12) -- it believes older Safari
// has a bug it cannot work around -- so the choice is between destructuring and
// the low floor. The floor wins: an iPhone too old for this is still somebody
// trying to join a mailing list, and `options.bare` costs nothing.

// Per-page counter, so two widgets on one page never share an element id and
// therefore never cross-wire their labels.
let sequence = 0

export function nextId() {
  sequence += 1
  return `mk-signup-${sequence}`
}

// In bare mode the widget emits NO classes at all, rather than a scoping class
// that turns our rules off. That way, on a page holding one styled form and one
// bare one, the shared sheet simply cannot match the bare one -- no :where(),
// no extra selector, and no chance of a rule leaking across.
export function classer(bare) {
  return (name) => (bare ? "" : name)
}

export function el(tag, options) {
  const opts = options || {}
  const node = document.createElement(tag)

  if (opts.className) node.className = opts.className
  if (opts.text !== undefined && opts.text !== null) node.textContent = opts.text

  const attrs = opts.attrs
  if (attrs) {
    Object.keys(attrs).forEach((key) => {
      if (attrs[key] !== null && attrs[key] !== undefined) node.setAttribute(key, attrs[key])
    })
  }

  return node
}

// A placeholder with the real form's box metrics, so the customer's page does
// not jump when the config lands. aria-hidden because a screen reader has
// nothing to say about four empty boxes, and no animation: motion on somebody
// else's page is presumptuous and costs a compositor layer.
//
// It cannot know whether the phone field is on, so the generated snippet
// carries data-mk-fields as a hint. A stale hint costs one small reflow and can
// never render a wrong form, because it draws no text.
export function renderSkeleton(container, options) {
  // With no stylesheet there is nothing to draw a grey box with, and a stack of
  // empty divs is a shapeless blob. Better to occupy no space at all.
  if (options.bare) return

  const cls = classer(false)
  const wrap = el("div", {attrs: {"aria-hidden": "true"}})
  const bar = (style) => el("div", {className: cls("mk-signup__skeleton"), attrs: {style: style}})

  wrap.appendChild(bar("height: 1.4em; width: 55%; margin-bottom: 1em"))
  options.fields.forEach(() => {
    wrap.appendChild(bar("height: 1em; width: 35%; margin-bottom: 0.35em"))
    wrap.appendChild(bar("height: 2.6em; margin-bottom: 0.75em"))
  })
  wrap.appendChild(bar("height: 2.8em"))

  container.appendChild(wrap)
}

export function renderForm(config, options) {
  const cls = classer(options.bare)
  const id = nextId()
  const form = el("form", {
    className: cls("mk-signup__form"),
    attrs: {novalidate: "", "data-mk": "form"}
  })
  const refs = {form: form, inputs: {}, errors: {}}

  if (config.heading) {
    // A heading level the host page chooses. A widget cannot know somebody
    // else's document outline, and hardcoding <h1> inside their page body is an
    // outline break we would inflict on every customer.
    form.appendChild(
      el(`h${options.headingLevel}`, {
        className: cls("mk-signup__heading"),
        text: config.heading,
        attrs: {"data-mk": "heading"}
      })
    )
  }

  if (config.description) {
    form.appendChild(
      el("p", {
        className: cls("mk-signup__intro"),
        text: config.description,
        attrs: {"data-mk": "intro"}
      })
    )
  }

  // Always present, always empty until it is not. A live region added to the
  // document at the moment of the error is not announced.
  refs.alert = el("div", {
    className: cls("mk-signup__alert"),
    attrs: {role: "alert", "data-mk": "alert"}
  })
  form.appendChild(refs.alert)

  const fields = config.fields || []
  fields.forEach((field) => {
    form.appendChild(renderField(field, cls, id, refs, config.optional_marker))

    // The consent box belongs immediately under the number it is about.
    if (field.name === "phone" && config.consent) {
      form.appendChild(renderConsent(config, cls, id, refs))
    }
  })

  const segments = config.segments || []
  if (segments.length) form.appendChild(renderSegments(config, cls, id, refs))

  form.appendChild(renderGuards(config, refs))

  refs.button = el("button", {
    className: cls("mk-signup__button"),
    text: config.button_label,
    attrs: {type: "submit", "data-mk": "submit"}
  })
  form.appendChild(refs.button)

  const disclosures = config.disclosures || []
  if (disclosures.length) form.appendChild(renderDisclosures(config, cls, id, refs))

  if (config.email_note) {
    form.appendChild(
      el("p", {
        className: cls("mk-signup__disclosure"),
        text: config.email_note,
        attrs: {"data-mk": "email-note"}
      })
    )
  }

  return refs
}

function renderField(field, cls, id, refs, optionalMarker) {
  const wrap = el("div", {className: cls("mk-signup__field")})
  const fieldId = `${id}-${field.name}`
  const describedBy = []

  // The same rule the hosted page follows: any field that is not required says
  // so. Both surfaces read it off `required`, so neither can drift.
  const label = el("label", {
    className: cls("mk-signup__label"),
    text: field.label,
    attrs: {for: fieldId}
  })
  if (!field.required && optionalMarker) {
    label.appendChild(document.createTextNode(` ${optionalMarker}`))
  }
  wrap.appendChild(label)

  const input = el("input", {
    className: cls("mk-signup__input"),
    attrs: {
      type: field.type,
      name: field.name,
      id: fieldId,
      autocomplete: field.autocomplete,
      inputmode: field.type === "tel" ? "tel" : null,
      // Kept for semantics and for the mobile keyboard even though the form is
      // novalidate: what we do not want is the browser blocking submission with
      // its own bubble, in its own words, which is copy we do not control.
      required: field.required ? "" : null,
      "data-mk": `${field.name}-input`
    }
  })
  wrap.appendChild(input)

  if (field.help) {
    const helpId = `${fieldId}-help`
    wrap.appendChild(
      el("span", {className: cls("mk-signup__hint"), text: field.help, attrs: {id: helpId}})
    )
    describedBy.push(helpId)
  }

  if (describedBy.length) input.setAttribute("aria-describedby", describedBy.join(" "))

  const error = el("span", {
    className: cls("mk-signup__error"),
    attrs: {id: `${fieldId}-error`, "data-mk": `${field.name}-error`}
  })
  wrap.appendChild(error)

  refs.inputs[field.name] = input
  refs.errors[field.name] = error
  return wrap
}

// The segments a form offers the visitor, one tick box each.
//
// role="group" + aria-labelledby rather than <fieldset><legend>, and that is
// not fussiness: a UA gives a fieldset a border and padding of its own, and in
// bare mode this widget emits NO CLASSES AT ALL -- so a fieldset would draw a
// box on the customer's page that no stylesheet of ours is allowed to remove.
// The same class of bug the honeypot's inline style exists to avoid.
//
// refs.segments is an ARRAY, and deliberately NOT a member of refs.inputs.
// widget.js#values() reads .value off everything in refs.inputs, and a
// checkbox's .value is its value attribute whether or not it is ticked -- so a
// segment box parked there would submit every segment on the form, on every
// site that embeds one.
function renderSegments(config, cls, id, refs) {
  const groupId = `${id}-segments`
  const choices = config.segments || []
  const wrap = el("div", {
    className: cls("mk-signup__segments"),
    attrs: {role: "group", "aria-labelledby": `${groupId}-label`, "data-mk": "segments"}
  })

  wrap.appendChild(
    el("span", {
      className: cls("mk-signup__segments-label"),
      text: config.segments_label,
      attrs: {id: `${groupId}-label`}
    })
  )

  refs.segments = []

  choices.forEach((choice, index) => {
    const row = el("div", {className: cls("mk-signup__segment")})
    const boxId = `${groupId}-${index}`

    // Never `checked`, and nothing ticks one later -- the same rule
    // renderConsent states about the consent box.
    const box = el("input", {
      className: cls("mk-signup__checkbox"),
      attrs: {type: "checkbox", name: "segments[]", id: boxId, value: choice.id, "data-mk": "segment"}
    })
    row.appendChild(box)

    const label = el("label", {
      className: cls("mk-signup__segment-label"),
      text: choice.label,
      attrs: {for: boxId}
    })
    if (choice.description) {
      label.appendChild(
        el("span", {className: cls("mk-signup__hint"), text: choice.description})
      )
    }
    row.appendChild(label)

    refs.segments.push(box)
    wrap.appendChild(row)
  })

  if (config.segments_help) {
    wrap.appendChild(
      el("span", {className: cls("mk-signup__hint"), text: config.segments_help})
    )
  }

  return wrap
}

function renderConsent(config, cls, id, refs) {
  const wrap = el("div", {className: cls("mk-signup__consent")})
  const boxId = `${id}-consent`

  // No `checked`, ever, and nothing programmatically ticks it later. A
  // pre-ticked box is not consent.
  //
  // Deliberately not `required` either: on a form that also collects an email
  // the phone is optional, so the rule is conditional -- a number typed with
  // the box unticked is the invalid state, and validate.js owns that.
  const box = el("input", {
    className: cls("mk-signup__checkbox"),
    attrs: {type: "checkbox", name: config.consent.name, id: boxId, value: "1", "data-mk": "consent"}
  })

  wrap.appendChild(box)
  wrap.appendChild(
    el("label", {
      className: cls("mk-signup__consent-label"),
      text: config.consent.sentence,
      attrs: {for: boxId}
    })
  )

  refs.consent = box
  refs.consentError = el("span", {
    className: cls("mk-signup__error"),
    attrs: {id: `${boxId}-error`, "data-mk": "consent-error"}
  })
  wrap.appendChild(refs.consentError)

  return wrap
}

function renderDisclosures(config, cls, id, refs) {
  const wrap = el("div", {
    className: cls("mk-signup__disclosure"),
    attrs: {id: `${id}-disclosure`, "data-mk": "disclosure"}
  })

  config.disclosures.forEach((disclosure) => {
    const line = el("p", {className: cls("mk-signup__disclosure-line")})
    if (disclosure.term) line.appendChild(el("strong", {text: `${disclosure.term}: `}))
    line.appendChild(document.createTextNode(disclosure.text))
    wrap.appendChild(line)
  })

  const links = config.legal_links || []
  if (links.length) {
    const line = el("p", {className: cls("mk-signup__disclosure-line")})
    links.forEach((link, index) => {
      if (index > 0) line.appendChild(document.createTextNode(" | "))
      line.appendChild(
        el("a", {
          text: link.label,
          attrs: {href: absolute(link.url, config), target: "_blank", rel: "noopener"}
        })
      )
    })
    wrap.appendChild(line)
  }

  // The disclosures describe the CHECKBOX, not the page. This association is
  // the only thing that makes the message-frequency, rates and HELP/STOP text
  // reach a screen-reader user at the moment they are agreeing to it.
  if (refs.consent) refs.consent.setAttribute("aria-describedby", `${id}-disclosure`)

  return wrap
}

// The config gives paths; a form on somebody else's domain needs whole URLs.
function absolute(path, config) {
  try {
    return new URL(path, config.hosted_url).href
  } catch (error) {
    return path
  }
}

function renderGuards(config, refs) {
  const fragment = document.createDocumentFragment()

  // A real, visible-to-a-bot text input hidden off-screen -- not type="hidden",
  // which every bot framework knows to skip, and not `display: none` for the
  // same reason.
  //
  // Hidden by an INLINE STYLE, always, in both styled and bare modes. If this
  // lived in the optional stylesheet then setting data-mk-style="bare" would
  // turn it into a visible text box labelled "Website" on the customer's site,
  // which is the one bug bare mode would otherwise be certain to ship.
  const trap = el("div", {
    attrs: {
      style:
        "position: absolute; left: -9999px; top: auto; width: 1px; height: 1px; overflow: hidden",
      "aria-hidden": "true"
    }
  })
  trap.appendChild(
    el("input", {
      attrs: {
        type: "text",
        name: config.honeypot,
        tabindex: "-1",
        autocomplete: "off",
        "data-mk": "honeypot"
      }
    })
  )
  fragment.appendChild(trap)

  refs.stamp = el("input", {attrs: {type: "hidden", name: "stamp", value: config.stamp}})
  fragment.appendChild(refs.stamp)

  return fragment
}

// Focus moves here because the element the keyboard user was on -- the submit
// button -- has just left the document. Without it focus falls back to <body>
// and they are stranded at the top of a stranger's page.
//
// role="status" (polite) rather than "alert": this is a confirmation, not an
// interruption.
export function renderConfirmation(config, body, options) {
  const cls = classer(options.bare)
  const wrap = el("div", {
    className: cls("mk-signup__confirmation"),
    attrs: {role: "status", tabindex: "-1", "data-mk": "confirmation"}
  })

  const heading = body.heading || config.confirmation.heading
  if (heading) {
    wrap.appendChild(
      el(`h${options.headingLevel}`, {className: cls("mk-signup__heading"), text: heading})
    )
  }

  const messages = body.messages || []
  messages.forEach((message) => wrap.appendChild(el("p", {text: message})))

  const notes = body.notes || config.confirmation.notes || []
  notes.forEach((note) => {
    wrap.appendChild(el("p", {className: cls("mk-signup__disclosure"), text: note}))
  })

  return wrap
}
